import React, { useState, useEffect } from 'react';
import {
  format,
  addDays,
  addHours,
  setHours,
  setMinutes,
  isToday,
  isTomorrow,
  isPast,
  isSameDay,
  startOfDay,
  endOfDay,
  getMinutes,
  getHours,
  getDate,
  getMonth,
  getYear,
  parse,
  differenceInMinutes,
  isAfter,
  isBefore
} from 'date-fns';

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Helper function to get next 30-min slot
const getNextHalfHourSlot = (date) => {
  const minutes = getMinutes(date);
  const remainder = 30 - (minutes % 30);
  return addMinutes(date, remainder);
};

// Helper to generate time slots based on selected date (full day from 12:00 AM to 11:30 PM)
const generateTimeSlots = (selectedDate, fromDate = null) => {
  if (!selectedDate) return [];
  
  const slots = [];
  let startTime;
  
  if (isToday(selectedDate)) {
    // For today: start from next half hour slot
    startTime = getNextHalfHourSlot(fromDate || new Date());
    
    // Generate slots from startTime until 11:30 PM
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 30, 0, 0);
    
    let currentSlot = new Date(startTime);
    while (currentSlot <= endOfDay) {
      slots.push(format(currentSlot, 'h:mm a'));
      currentSlot = addMinutes(currentSlot, 30);
    }
  } else {
    // For tomorrow or custom date: start from 12:00 AM and go through entire day
    startTime = startOfDay(selectedDate);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 30, 0, 0);
    
    let currentSlot = new Date(startTime);
    while (currentSlot <= endOfDay) {
      slots.push(format(currentSlot, 'h:mm a'));
      currentSlot = addMinutes(currentSlot, 30);
    }
  }
  
  // If no slots available (all times past), show next day's slots
  if (slots.length === 0) {
    const nextDay = addDays(selectedDate, 1);
    const nextDayStart = startOfDay(nextDay);
    const nextDayEnd = new Date(nextDay);
    nextDayEnd.setHours(23, 30, 0, 0);
    
    let currentSlot = new Date(nextDayStart);
    while (currentSlot <= nextDayEnd) {
      slots.push(format(currentSlot, 'h:mm a'));
      currentSlot = addMinutes(currentSlot, 30);
    }
  }
  
  return slots;
};

// Helper to add minutes to a date
const addMinutes = (date, minutes) => {
  return new Date(date.getTime() + minutes * 60000);
};

// Helper to format date to MM/dd/yyyy
const formatDateToString = (date) => {
  if (!date) return "";
  return format(date, 'M/d/yyyy');
};

// Helper to format date to ISO string with time
const formatToISOString = (date, timeStr) => {
  if (!date || !timeStr) return "";
  
  try {
    // Parse the time string (e.g., "2:30 PM")
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 24-hour format
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    // Create new date with the time
    const dateTime = new Date(date);
    dateTime.setHours(hours, minutes, 0, 0);
    
    // Return ISO string
    return dateTime.toISOString();
  } catch (error) {
    console.error('Error formatting to ISO:', error);
    return "";
  }
};

// Helper to add 24 hours to a date
const add24Hours = (date) => {
  if (!date) return null;
  return addHours(date, 24);
};

// Helper to parse time string to Date object
const parseTimeToDate = (date, timeStr) => {
  if (!date || !timeStr) return null;
  
  try {
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    return setMinutes(setHours(new Date(date), hours), minutes);
  } catch (error) {
    console.error('Error parsing time:', error);
    return null;
  }
};

function Calendar({ onSelect, onClose, minDate = new Date(), selectedDate }) {
  const today = new Date();
  const [viewDate, setViewDate] = useState(selectedDate || today);
  const [picked, setPicked] = useState(null);

  const viewYear = getYear(viewDate);
  const viewMonth = getMonth(viewDate);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const prevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setPicked(null);
  };

  const nextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setPicked(null);
  };

  const isDatePast = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return isPast(date) && !isSameDay(date, today);
  };

  const isDateToday = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return isSameDay(date, today);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleConfirm = () => {
    if (!picked) return;
    const selectedDate = new Date(viewYear, viewMonth, picked);
    onSelect(selectedDate, format(selectedDate, 'MMM d'));
    onClose();
  };

  return (
    <div className="calendar-card">
        <div className="calendar-header">
          <button className="calendar-nav" onClick={prevMonth}>‹</button>
          <span className="calendar-month">{MONTHS[viewMonth]} {viewYear}</span>
          <button className="calendar-nav" onClick={nextMonth}>›</button>
        </div>

        <div className="calendar-weekdays">
          {WEEKDAYS.map(d => (
            <div key={d} className="calendar-weekday">{d}</div>
          ))}
        </div>

        <div className="calendar-days">
          {cells.map((day, i) => {
            if (!day) {
              return <div key={`e-${i}`} className="calendar-day empty" />;
            }
            
            const past = isDatePast(day);
            const sel = picked === day;
            const tod = isDateToday(day);
            
            let cls = "calendar-day";
            if (past) cls += " past";
            else if (sel) cls += " selected";
            else if (tod) cls += " today";
            
            return (
              <button 
                key={day} 
                className={cls} 
                onClick={() => !past && setPicked(day)}
                disabled={past}
              >
                {day}
              </button>
            );
          })}
        </div>

        <button 
          className="calendar-confirm" 
          onClick={handleConfirm} 
          disabled={!picked}
        >
          {picked
            ? `Confirm — ${MONTHS[viewMonth].slice(0, 3)} ${picked}`
            : "Select a day"}
        </button>
    </div>
  );
}

const DateTimeSelector = ({ onContinue }) => {
  const [activeSection, setActiveSection] = useState("dropoff");
  
  // Dropoff state
  const [dropoffDate, setDropoffDate] = useState(new Date());
  const [dropoffTime, setDropoffTime] = useState("");
  const [dropoffCustomLabel, setDropoffCustomLabel] = useState(null);
  const [showDropoffCalendar, setShowDropoffCalendar] = useState(false);
  const [dropoffTimeSlots, setDropoffTimeSlots] = useState([]);
  
  // Pickup state
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState("");
  const [pickupCustomLabel, setPickupCustomLabel] = useState(null);
  const [showPickupCalendar, setShowPickupCalendar] = useState(false);
  const [pickupTimeSlots, setPickupTimeSlots] = useState([]);

  // Initialize with current time
  useEffect(() => {
    const now = new Date();
    const after3Hours = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    
    // Set initial dropoff date to today
    setDropoffDate(now);
    
    // Generate time slots for today (from next half hour to 11:30 PM)
    const slots = generateTimeSlots(now, now);
    setDropoffTimeSlots(slots);
    setDropoffTime(slots[0]);
    
    // Set initial pickup (24 hours later)
    const pickupDateTime = add24Hours(now);
    setPickupDate(pickupDateTime);
    
    // Generate pickup time slots (full day from 12:00 AM to 11:30 PM)
    const pickupSlots = generateTimeSlots(pickupDateTime, pickupDateTime);
    setPickupTimeSlots(pickupSlots);
    setPickupTime(pickupSlots[0]);
  }, []);

  // Update pickup when dropoff changes
  useEffect(() => {
    if (dropoffDate && dropoffTime) {
      const dropoffDateTime = parseTimeToDate(dropoffDate, dropoffTime);
      if (dropoffDateTime) {
        const newPickupDate = add24Hours(dropoffDateTime);
        setPickupDate(newPickupDate);
        
        // Generate new pickup time slots (full day)
        const slots = generateTimeSlots(newPickupDate, newPickupDate);
        setPickupTimeSlots(slots);
        
        // Set pickup time to the same relative time if available
        const dropoffHour = getHours(dropoffDateTime);
        const dropoffMinute = getMinutes(dropoffDateTime);
        
        const matchingSlot = slots.find(slot => {
          const [time, period] = slot.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          return hours === dropoffHour && minutes === dropoffMinute;
        });
        
        setPickupTime(matchingSlot || slots[0]);
      }
    }
  }, [dropoffDate, dropoffTime]);

  const dropDates = [
    { label: "Today", value: "today" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "📅 Custom", value: "custom" },
  ];

  const handleDropoffDatePill = (value) => {
    if (value === "today") {
      const today = new Date();
      setDropoffDate(today);
      setDropoffCustomLabel(null);
      setShowDropoffCalendar(false);
      
      // Generate time slots for today (from next half hour to 11:30 PM)
      const slots = generateTimeSlots(today, today);
      setDropoffTimeSlots(slots);
      if (slots.length > 0) {
        setDropoffTime(slots[0]);
      }
    } else if (value === "tomorrow") {
      const tomorrow = addDays(new Date(), 1);
      setDropoffDate(tomorrow);
      setDropoffCustomLabel(null);
      setShowDropoffCalendar(false);
      
      // Generate time slots for tomorrow (full day from 12:00 AM to 11:30 PM)
      const slots = generateTimeSlots(tomorrow);
      setDropoffTimeSlots(slots);
      if (slots.length > 0) {
        setDropoffTime(slots[0]); // Will be 12:00 AM
      }
    } else if (value === "custom") {
      setShowDropoffCalendar(true);
    }
  };

  const handlePickupDatePill = (value) => {
    if (value === "today") {
      const today = new Date();
      setPickupDate(today);
      setPickupCustomLabel(null);
      setShowPickupCalendar(false);
      
      // Generate time slots for today (from next half hour to 11:30 PM)
      const slots = generateTimeSlots(today, today);
      setPickupTimeSlots(slots);
      if (slots.length > 0) {
        setPickupTime(slots[0]);
      }
    } else if (value === "tomorrow") {
      const tomorrow = addDays(new Date(), 1);
      setPickupDate(tomorrow);
      setPickupCustomLabel(null);
      setShowPickupCalendar(false);
      
      // Generate time slots for tomorrow (full day from 12:00 AM to 11:30 PM)
      const slots = generateTimeSlots(tomorrow);
      setPickupTimeSlots(slots);
      if (slots.length > 0) {
        setPickupTime(slots[0]); // Will be 12:00 AM
      }
    } else if (value === "custom") {
      setShowPickupCalendar(true);
    }
  };

  const handleDropoffCalendarSelect = (date, label) => {
    setDropoffDate(date);
    setDropoffCustomLabel(label);
    setShowDropoffCalendar(false);
    
    // Generate time slots for the selected date (full day from 12:00 AM to 11:30 PM)
    const slots = generateTimeSlots(date);
    setDropoffTimeSlots(slots);
    if (slots.length > 0) {
      setDropoffTime(slots[0]); // Will be 12:00 AM
    }
  };

  const handlePickupCalendarSelect = (date, label) => {
    setPickupDate(date);
    setPickupCustomLabel(label);
    setShowPickupCalendar(false);
    
    // Generate time slots for the selected date (full day from 12:00 AM to 11:30 PM)
    const slots = generateTimeSlots(date);
    setPickupTimeSlots(slots);
    if (slots.length > 0) {
      setPickupTime(slots[0]);
    }
  };

  const handleContinue = () => {
    // Create ISO strings for reserve_start and reserve_end
    const reserveStart = formatToISOString(dropoffDate, dropoffTime);
    const reserveEnd = formatToISOString(pickupDate, pickupTime);
    
    const dateTimeData = {
      reserve_start: reserveStart,
      reserve_end: reserveEnd
    };
    
    if (onContinue) {
      onContinue(dateTimeData);
    }

    console.log("date is: ", dateTimeData);
  };

  const handleQuickSelect = () => {
    const now = new Date();
    const nextSlot = getNextHalfHourSlot(now);
    const timeStr = format(nextSlot, 'h:mm a');
    
    setDropoffDate(now);
    setDropoffTime(timeStr);
    setDropoffCustomLabel(null);
    setShowDropoffCalendar(false);
    
    // Regenerate time slots for today
    const slots = generateTimeSlots(now, now);
    setDropoffTimeSlots(slots);
    
    // Auto update pickup
    const newPickupDate = add24Hours(now);
    setPickupDate(newPickupDate);
    
    // Generate pickup time slots (full day)
    const pickupSlots = generateTimeSlots(newPickupDate, newPickupDate);
    setPickupTimeSlots(pickupSlots);
    
    // Set pickup time to same relative time
    const dropoffHour = getHours(nextSlot);
    const dropoffMinute = getMinutes(nextSlot);
    
    const matchingSlot = pickupSlots.find(slot => {
      const [time, period] = slot.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return hours === dropoffHour && minutes === dropoffMinute;
    });
    
    setPickupTime(matchingSlot || pickupSlots[0]);
  };

  const formatDisplayDate = (date) => {
    if (!date) return "";
    
    if (isToday(date)) {
      return `Today, ${format(date, 'M/d')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'M/d')}`;
    } else {
      return format(date, 'EEE, M/d');
    }
  };

  const dropOffDisplay = dropoffDate && dropoffTime ? `${formatDisplayDate(dropoffDate)}, ${dropoffTime}` : "Select dropoff time";
  const pickUpDisplay = pickupDate && pickupTime ? `${formatDisplayDate(pickupDate)}, ${pickupTime}` : "Select pickup time";

  return (
    // <div className="date-time-selector">
    //   <div className="date-time-selector__phone-shell">
    //     {/* Scrollable Content */}
    //     <div className="date-time-selector__scroll-area">
    //       <p className="date-time-selector__description">
    //         Let the store staff know when you want to drop off your bags and collect them back. 
    //         Bookings are paid for 24 hours.
    //       </p>

    //       {/* Summary Card */}
    //       <div className="date-time-selector__summary-card">
    //         <div 
    //           className={`date-time-selector__summary-row ${
    //             activeSection === "dropoff" ? "date-time-selector__summary-row--active" : ""
    //           }`} 
    //           onClick={() => setActiveSection("dropoff")}
    //         >
    //           <div className="date-time-selector__summary-left">
    //             <div className={`date-time-selector__dot ${
    //               activeSection === "dropoff" ? "date-time-selector__dot--active" : ""
    //             }`} />
    //             <span className="date-time-selector__summary-label">Drop Off</span>
    //           </div>
    //           <span className="date-time-selector__summary-value">{dropOffDisplay}</span>
    //         </div>
            
    //         <div 
    //           className={`date-time-selector__summary-row ${
    //             activeSection === "pickup" ? "date-time-selector__summary-row--active" : ""
    //           }`} 
    //           onClick={() => setActiveSection("pickup")}
    //         >
    //           <div className="date-time-selector__summary-left">
    //             <div className={`date-time-selector__dot ${
    //               activeSection === "pickup" ? "date-time-selector__dot--active" : ""
    //             }`} />
    //             <span className="date-time-selector__summary-label">Pick Up</span>
    //           </div>
    //           <span className="date-time-selector__summary-value">{pickUpDisplay}</span>
    //         </div>
    //       </div>

    //       {/* Dropoff Section */}
    //       {activeSection === "dropoff" && (
    //         <>
    //           <div className="date-time-selector__section-label">DROPOFF DATE</div>
    //           <div className="date-time-selector__date-row">
    //             {dropDates.map((d) => {
    //               let isSelected = false;
                  
    //               if (d.value === "today") {
    //                 isSelected = isToday(dropoffDate);
    //               } else if (d.value === "tomorrow") {
    //                 isSelected = isTomorrow(dropoffDate);
    //               } else if (d.value === "custom") {
    //                 isSelected = dropoffCustomLabel !== null && !isToday(dropoffDate) && !isTomorrow(dropoffDate);
    //               }
                  
    //               return (
    //                 <div
    //                   key={d.value}
    //                   className={`date-time-selector__date-pill ${
    //                     isSelected ? "date-time-selector__date-pill--selected" : ""
    //                   }`}
    //                   onClick={() => handleDropoffDatePill(d.value)}
    //                 >
    //                   <div className="date-time-selector__date-pill-label">
    //                     {d.value === "custom" && dropoffCustomLabel 
    //                       ? dropoffCustomLabel
    //                       : d.label}
    //                   </div>
    //                 </div>
    //               );
    //             })}
    //           </div>

    //           {/* Dropoff Calendar */}
    //           {showDropoffCalendar && (
    //             <Calendar
    //               onSelect={handleDropoffCalendarSelect}
    //               onClose={() => setShowDropoffCalendar(false)}
    //               minDate={new Date()}
    //               selectedDate={dropoffDate}
    //             />
    //           )}

    //           {/* Dropoff Time Selection */}
    //           <div className="date-time-selector__section-label">
    //             DROPOFF TIME <span>(Mumbai time)</span>
    //           </div>
    //           <div className="date-time-selector__time-list-container">
    //             <div className="date-time-selector__time-list">
    //               {dropoffTimeSlots.map((t) => (
    //                 <button
    //                   key={t}
    //                   className={`date-time-selector__time-slot ${
    //                     dropoffTime === t ? "date-time-selector__time-slot--selected" : ""
    //                   }`}
    //                   onClick={() => setDropoffTime(t)}
    //                 >
    //                   {t}
    //                 </button>
    //               ))}
    //             </div>
    //           </div>
    //         </>
    //       )}

    //       {/* Pickup Section */}
    //       {activeSection === "pickup" && (
    //         <>
    //           <div className="date-time-selector__section-label">PICKUP DATE</div>
    //           <div className="date-time-selector__date-row">
    //             {dropDates.map((d) => {
    //               let isSelected = false;
                  
    //               if (d.value === "today") {
    //                 isSelected = isToday(pickupDate);
    //               } else if (d.value === "tomorrow") {
    //                 isSelected = isTomorrow(pickupDate);
    //               } else if (d.value === "custom") {
    //                 isSelected = pickupCustomLabel !== null && !isToday(pickupDate) && !isTomorrow(pickupDate);
    //               }
                  
    //               return (
    //                 <div
    //                   key={d.value}
    //                   className={`date-time-selector__date-pill ${
    //                     isSelected ? "date-time-selector__date-pill--selected" : ""
    //                   }`}
    //                   onClick={() => handlePickupDatePill(d.value)}
    //                 >
    //                   <div className="date-time-selector__date-pill-label">
    //                     {d.value === "custom" && pickupCustomLabel 
    //                       ? pickupCustomLabel
    //                       : d.label}
    //                   </div>
    //                 </div>
    //               );
    //             })}
    //           </div>

    //           {/* Pickup Calendar */}
    //           {showPickupCalendar && (
    //             <Calendar
    //               onSelect={handlePickupCalendarSelect}
    //               onClose={() => setShowPickupCalendar(false)}
    //               minDate={new Date()}
    //               selectedDate={pickupDate}
    //             />
    //           )}

    //           {/* Pickup Time Selection */}
    //           <div className="date-time-selector__section-label">
    //             PICKUP TIME <span>(Mumbai time)</span>
    //           </div>
    //           <div className="date-time-selector__time-list-container">
    //             <div className="date-time-selector__time-list">
    //               {pickupTimeSlots.map((t) => (
    //                 <button
    //                   key={t}
    //                   className={`date-time-selector__time-slot ${
    //                     pickupTime === t ? "date-time-selector__time-slot--selected" : ""
    //                   }`}
    //                   onClick={() => setPickupTime(t)}
    //                 >
    //                   {t}
    //                 </button>
    //               ))}
    //             </div>
    //           </div>
    //         </>
    //       )}
    //     </div>

    //     {/* Bottom Area */}
    //     <div className="date-time-selector__bottom-area">
    //       <button 
    //         className="date-time-selector__quick-select-btn"
    //         onClick={handleQuickSelect}
    //       >
    //         {/* Today, Now → {pickUpDisplay} */}
    //         {dropOffDisplay}→ {pickUpDisplay}
    //       </button>
    //       <button 
    //         className="date-time-selector__continue-btn"
    //         onClick={handleContinue}
    //       >
    //         Continue
    //       </button>
    //     </div>
    //   </div>
    // </div>
    <div className="date-time-selector">
  <div className="phone-shell">
    {/* Scrollable Content */}
    <div className="scroll-area">
      <p className="description">
        Let the store staff know when you want to drop off your bags and collect them back. 
        Bookings are paid for 24 hours.
      </p>

      {/* Summary Card */}
      <div className="summary-card">
        <div 
          className={`summary-row ${activeSection === "dropoff" ? "active" : ""}`} 
          onClick={() => setActiveSection("dropoff")}
        >
          <div className="summary-left">
            <div className={`dot ${activeSection === "dropoff" ? "active" : ""}`} />
            <span className="summary-label">Drop Off</span>
          </div>
          <span className="summary-value">{dropOffDisplay}</span>
        </div>
        
        <div 
          className={`summary-row ${activeSection === "pickup" ? "active" : ""}`} 
          onClick={() => setActiveSection("pickup")}
        >
          <div className="summary-left">
            <div className={`dot ${activeSection === "pickup" ? "active" : ""}`} />
            <span className="summary-label">Pick Up</span>
          </div>
          <span className="summary-value">{pickUpDisplay}</span>
        </div>
      </div>

      {/* Dropoff Section */}
      {activeSection === "dropoff" && (
        <>
          <div className="section-label">DROPOFF DATE</div>
          <div className="date-row">
            {dropDates.map((d) => {
              let isSelected = false;
              
              if (d.value === "today") {
                isSelected = isToday(dropoffDate);
              } else if (d.value === "tomorrow") {
                isSelected = isTomorrow(dropoffDate);
              } else if (d.value === "custom") {
                isSelected = dropoffCustomLabel !== null && !isToday(dropoffDate) && !isTomorrow(dropoffDate);
              }
              
              return (
                <div
                  key={d.value}
                  className={`date-pill ${isSelected ? "selected" : ""}`}
                  onClick={() => handleDropoffDatePill(d.value)}
                >
                  <div className="date-pill-label">
                    {d.value === "custom" && dropoffCustomLabel 
                      ? dropoffCustomLabel
                      : d.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dropoff Calendar */}
          {showDropoffCalendar && (
            <Calendar
              onSelect={handleDropoffCalendarSelect}
              onClose={() => setShowDropoffCalendar(false)}
              minDate={new Date()}
              selectedDate={dropoffDate}
            />
          )}

          {/* Dropoff Time Selection */}
          <div className="section-label">
            DROPOFF TIME <span>(Mumbai time)</span>
          </div>
          <div className="time-list-container">
            <div className="time-list">
              {dropoffTimeSlots.map((t) => (
                <button
                  key={t}
                  className={`time-slot ${dropoffTime === t ? "selected" : ""}`}
                  onClick={() => setDropoffTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Pickup Section */}
      {activeSection === "pickup" && (
        <>
          <div className="section-label">PICKUP DATE</div>
          <div className="date-row">
            {dropDates.map((d) => {
              let isSelected = false;
              
              if (d.value === "today") {
                isSelected = isToday(pickupDate);
              } else if (d.value === "tomorrow") {
                isSelected = isTomorrow(pickupDate);
              } else if (d.value === "custom") {
                isSelected = pickupCustomLabel !== null && !isToday(pickupDate) && !isTomorrow(pickupDate);
              }
              
              return (
                <div
                  key={d.value}
                  className={`date-pill ${isSelected ? "selected" : ""}`}
                  onClick={() => handlePickupDatePill(d.value)}
                >
                  <div className="date-pill-label">
                    {d.value === "custom" && pickupCustomLabel 
                      ? pickupCustomLabel
                      : d.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pickup Calendar */}
          {showPickupCalendar && (
            <Calendar
              onSelect={handlePickupCalendarSelect}
              onClose={() => setShowPickupCalendar(false)}
              minDate={new Date()}
              selectedDate={pickupDate}
            />
          )}

          {/* Pickup Time Selection */}
          <div className="section-label">
            PICKUP TIME <span>(Mumbai time)</span>
          </div>
          <div className="time-list-container">
            <div className="time-list">
              {pickupTimeSlots.map((t) => (
                <button
                  key={t}
                  className={`time-slot ${pickupTime === t ? "selected" : ""}`}
                  onClick={() => setPickupTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>

    {/* Bottom Area */}
    <div className="bottom-area">
      <button 
        className="quick-select-btn"
        onClick={handleQuickSelect}
      >
        {dropOffDisplay} → {pickUpDisplay}
      </button>
      <button 
        className="continue-btn"
        onClick={handleContinue}
      >
        Continue
      </button>
    </div>
  </div>
</div>
  );
};

// const DateTimeSelector = ({ onContinue }) => {
//   const [activeSection, setActiveSection] = useState("dropoff");
  
//   // Dropoff state
//   const [dropoffDate, setDropoffDate] = useState(new Date());
//   const [dropoffTime, setDropoffTime] = useState("");
//   const [dropoffCustomLabel, setDropoffCustomLabel] = useState(null);
//   const [showDropoffCalendar, setShowDropoffCalendar] = useState(false);
//   const [dropoffTimeSlots, setDropoffTimeSlots] = useState([]);
  
//   // Pickup state
//   const [pickupDate, setPickupDate] = useState(new Date());
//   const [pickupTime, setPickupTime] = useState("");
//   const [pickupCustomLabel, setPickupCustomLabel] = useState(null);
//   const [showPickupCalendar, setShowPickupCalendar] = useState(false);
//   const [pickupTimeSlots, setPickupTimeSlots] = useState([]);

//   // Initialize with current time
//   useEffect(() => {
//     const now = new Date();
//     const after3Hours = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    
//     // Set initial dropoff date to today
//     setDropoffDate(now);
    
//     // Generate time slots for today starting from 3 hours after current time
//     const slots = generateTimeSlots(now, after3Hours);
//     setDropoffTimeSlots(slots);
//     setDropoffTime(slots[0]);
    
//     // Set initial pickup (24 hours later)
//     const pickupDateTime = add24Hours(now);
//     setPickupDate(pickupDateTime);
    
//     // Generate pickup time slots (full day from 12:00 AM to 11:30 PM)
//     const pickupSlots = generateTimeSlots(pickupDateTime, pickupDateTime);
//     setPickupTimeSlots(pickupSlots);
//     setPickupTime(pickupSlots[0]);
//   }, []);

//   // Update pickup when dropoff changes
//   useEffect(() => {
//     if (dropoffDate && dropoffTime) {
//       const dropoffDateTime = parseTimeToDate(dropoffDate, dropoffTime);
//       if (dropoffDateTime) {
//         const newPickupDate = add24Hours(dropoffDateTime);
//         setPickupDate(newPickupDate);
        
//         // Generate new pickup time slots (full day)
//         const slots = generateTimeSlots(newPickupDate, newPickupDate);
//         setPickupTimeSlots(slots);
        
//         // Set pickup time to the same relative time if available
//         const dropoffHour = getHours(dropoffDateTime);
//         const dropoffMinute = getMinutes(dropoffDateTime);
        
//         const matchingSlot = slots.find(slot => {
//           const [time, period] = slot.split(' ');
//           let [hours, minutes] = time.split(':').map(Number);
//           if (period === 'PM' && hours !== 12) hours += 12;
//           if (period === 'AM' && hours === 12) hours = 0;
//           return hours === dropoffHour && minutes === dropoffMinute;
//         });
        
//         setPickupTime(matchingSlot || slots[0]);
//       }
//     }
//   }, [dropoffDate, dropoffTime]);

//   const dropDates = [
//     { label: "Today", value: "today" },
//     { label: "Tomorrow", value: "tomorrow" },
//     { label: "📅 Custom", value: "custom" },
//   ];

//   const handleDropoffDatePill = (value) => {
//     if (value === "today") {
//       const today = new Date();
//       // Calculate time 3 hours from now
//       const after3Hours = new Date(today.getTime() + 3 * 60 * 60 * 1000);
      
//       setDropoffDate(today);
//       setDropoffCustomLabel(null);
//       setShowDropoffCalendar(false);
      
//       // Generate time slots for today starting from 3 hours after current time
//       const slots = generateTimeSlots(today, after3Hours);
//       setDropoffTimeSlots(slots);
//       if (slots.length > 0) {
//         setDropoffTime(slots[0]);
//       }
//     } else if (value === "tomorrow") {
//       const tomorrow = addDays(new Date(), 1);
//       setDropoffDate(tomorrow);
//       setDropoffCustomLabel(null);
//       setShowDropoffCalendar(false);
      
//       // Generate time slots for tomorrow (full day from 12:00 AM to 11:30 PM)
//       const slots = generateTimeSlots(tomorrow);
//       setDropoffTimeSlots(slots);
//       if (slots.length > 0) {
//         setDropoffTime(slots[0]); // Will be 12:00 AM
//       }
//     } else if (value === "custom") {
//       setShowDropoffCalendar(true);
//     }
//   };

//   const handlePickupDatePill = (value) => {
//     if (value === "today") {
//       const today = new Date();
//       setPickupDate(today);
//       setPickupCustomLabel(null);
//       setShowPickupCalendar(false);
      
//       // Generate time slots for today (from next half hour to 11:30 PM)
//       const slots = generateTimeSlots(today, today);
//       setPickupTimeSlots(slots);
//       if (slots.length > 0) {
//         setPickupTime(slots[0]);
//       }
//     } else if (value === "tomorrow") {
//       const tomorrow = addDays(new Date(), 1);
//       setPickupDate(tomorrow);
//       setPickupCustomLabel(null);
//       setShowPickupCalendar(false);
      
//       // Generate time slots for tomorrow (full day from 12:00 AM to 11:30 PM)
//       const slots = generateTimeSlots(tomorrow);
//       setPickupTimeSlots(slots);
//       if (slots.length > 0) {
//         setPickupTime(slots[0]); // Will be 12:00 AM
//       }
//     } else if (value === "custom") {
//       setShowPickupCalendar(true);
//     }
//   };

//   const handleDropoffCalendarSelect = (date, label) => {
//     setDropoffDate(date);
//     setDropoffCustomLabel(label);
//     setShowDropoffCalendar(false);
    
//     // Generate time slots for the selected date (full day from 12:00 AM to 11:30 PM)
//     const slots = generateTimeSlots(date);
//     setDropoffTimeSlots(slots);
//     if (slots.length > 0) {
//       setDropoffTime(slots[0]); // Will be 12:00 AM
//     }
//   };

//   const handlePickupCalendarSelect = (date, label) => {
//     setPickupDate(date);
//     setPickupCustomLabel(label);
//     setShowPickupCalendar(false);
    
//     // Generate time slots for the selected date (full day from 12:00 AM to 11:30 PM)
//     const slots = generateTimeSlots(date);
//     setPickupTimeSlots(slots);
//     if (slots.length > 0) {
//       setPickupTime(slots[0]);
//     }
//   };

//   const handleContinue = () => {
//     // Create ISO strings for reserve_start and reserve_end
//     const reserveStart = formatToISOString(dropoffDate, dropoffTime);
//     const reserveEnd = formatToISOString(pickupDate, pickupTime);
    
//     const dateTimeData = {
//       reserve_start: reserveStart,
//       reserve_end: reserveEnd
//     };
    
//     if (onContinue) {
//       onContinue(dateTimeData);
//     }

//     console.log("date is: ", dateTimeData);
//   };

//   const handleQuickSelect = () => {
//     const now = new Date();
//     // Calculate time 3 hours from now
//     const after3Hours = new Date(now.getTime() + 3 * 60 * 60 * 1000);
//     const nextSlot = getNextHalfHourSlot(after3Hours);
//     const timeStr = format(nextSlot, 'h:mm a');
    
//     setDropoffDate(now);
//     setDropoffTime(timeStr);
//     setDropoffCustomLabel(null);
//     setShowDropoffCalendar(false);
    
//     // Regenerate time slots for today starting from 3 hours later
//     const slots = generateTimeSlots(now, after3Hours);
//     setDropoffTimeSlots(slots);
    
//     // Auto update pickup (24 hours after dropoff)
//     const dropoffDateTime = parseTimeToDate(now, timeStr);
//     const newPickupDate = add24Hours(dropoffDateTime || now);
//     setPickupDate(newPickupDate);
    
//     // Generate pickup time slots (full day)
//     const pickupSlots = generateTimeSlots(newPickupDate, newPickupDate);
//     setPickupTimeSlots(pickupSlots);
    
//     // Set pickup time to same relative time
//     const dropoffHour = getHours(nextSlot);
//     const dropoffMinute = getMinutes(nextSlot);
    
//     const matchingSlot = pickupSlots.find(slot => {
//       const [time, period] = slot.split(' ');
//       let [hours, minutes] = time.split(':').map(Number);
//       if (period === 'PM' && hours !== 12) hours += 12;
//       if (period === 'AM' && hours === 12) hours = 0;
//       return hours === dropoffHour && minutes === dropoffMinute;
//     });
    
//     setPickupTime(matchingSlot || pickupSlots[0]);
//   };

//   const formatDisplayDate = (date) => {
//     if (!date) return "";
    
//     if (isToday(date)) {
//       return `Today, ${format(date, 'M/d')}`;
//     } else if (isTomorrow(date)) {
//       return `Tomorrow, ${format(date, 'M/d')}`;
//     } else {
//       return format(date, 'EEE, M/d');
//     }
//   };

//   const dropOffDisplay = dropoffDate && dropoffTime ? `${formatDisplayDate(dropoffDate)}, ${dropoffTime}` : "Select dropoff time";
//   const pickUpDisplay = pickupDate && pickupTime ? `${formatDisplayDate(pickupDate)}, ${pickupTime}` : "Select pickup time";

//   return (
//     <div className="date-time-selector">
//       <div className="date-time-selector__phone-shell">
//         {/* Scrollable Content */}
//         <div className="date-time-selector__scroll-area">
//           <p className="date-time-selector__description">
//             Let the store staff know when you want to drop off your bags and collect them back. 
//             Bookings are paid for 24 hours.
//           </p>

//           {/* Summary Card */}
//           <div className="date-time-selector__summary-card">
//             <div 
//               className={`date-time-selector__summary-row ${
//                 activeSection === "dropoff" ? "date-time-selector__summary-row--active" : ""
//               }`} 
//               onClick={() => setActiveSection("dropoff")}
//             >
//               <div className="date-time-selector__summary-left">
//                 <div className={`date-time-selector__dot ${
//                   activeSection === "dropoff" ? "date-time-selector__dot--active" : ""
//                 }`} />
//                 <span className="date-time-selector__summary-label">Drop Off</span>
//               </div>
//               <span className="date-time-selector__summary-value">{dropOffDisplay}</span>
//             </div>
            
//             <div 
//               className={`date-time-selector__summary-row ${
//                 activeSection === "pickup" ? "date-time-selector__summary-row--active" : ""
//               }`} 
//               onClick={() => setActiveSection("pickup")}
//             >
//               <div className="date-time-selector__summary-left">
//                 <div className={`date-time-selector__dot ${
//                   activeSection === "pickup" ? "date-time-selector__dot--active" : ""
//                 }`} />
//                 <span className="date-time-selector__summary-label">Pick Up</span>
//               </div>
//               <span className="date-time-selector__summary-value">{pickUpDisplay}</span>
//             </div>
//           </div>

//           {/* Dropoff Section */}
//           {activeSection === "dropoff" && (
//             <>
//               <div className="date-time-selector__section-label">DROPOFF DATE</div>
//               <div className="date-time-selector__date-row">
//                 {dropDates.map((d) => {
//                   let isSelected = false;
                  
//                   if (d.value === "today") {
//                     isSelected = isToday(dropoffDate);
//                   } else if (d.value === "tomorrow") {
//                     isSelected = isTomorrow(dropoffDate);
//                   } else if (d.value === "custom") {
//                     isSelected = dropoffCustomLabel !== null && !isToday(dropoffDate) && !isTomorrow(dropoffDate);
//                   }
                  
//                   return (
//                     <div
//                       key={d.value}
//                       className={`date-time-selector__date-pill ${
//                         isSelected ? "date-time-selector__date-pill--selected" : ""
//                       }`}
//                       onClick={() => handleDropoffDatePill(d.value)}
//                     >
//                       <div className="date-time-selector__date-pill-label">
//                         {d.value === "custom" && dropoffCustomLabel 
//                           ? dropoffCustomLabel
//                           : d.label}
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>

//               {/* Dropoff Calendar */}
//               {showDropoffCalendar && (
//                 <Calendar
//                   onSelect={handleDropoffCalendarSelect}
//                   onClose={() => setShowDropoffCalendar(false)}
//                   minDate={new Date()}
//                   selectedDate={dropoffDate}
//                 />
//               )}

//               {/* Dropoff Time Selection */}
//               <div className="date-time-selector__section-label">
//                 DROPOFF TIME <span>(Mumbai time)</span>
//               </div>
//               <div className="date-time-selector__time-list-container">
//                 <div className="date-time-selector__time-list">
//                   {dropoffTimeSlots.map((t) => (
//                     <button
//                       key={t}
//                       className={`date-time-selector__time-slot ${
//                         dropoffTime === t ? "date-time-selector__time-slot--selected" : ""
//                       }`}
//                       onClick={() => setDropoffTime(t)}
//                     >
//                       {t}
//                     </button>
//                   ))}
//                 </div>
//               </div>
//             </>
//           )}

//           {/* Pickup Section */}
//           {activeSection === "pickup" && (
//             <>
//               <div className="date-time-selector__section-label">PICKUP DATE</div>
//               <div className="date-time-selector__date-row">
//                 {dropDates.map((d) => {
//                   let isSelected = false;
                  
//                   if (d.value === "today") {
//                     isSelected = isToday(pickupDate);
//                   } else if (d.value === "tomorrow") {
//                     isSelected = isTomorrow(pickupDate);
//                   } else if (d.value === "custom") {
//                     isSelected = pickupCustomLabel !== null && !isToday(pickupDate) && !isTomorrow(pickupDate);
//                   }
                  
//                   return (
//                     <div
//                       key={d.value}
//                       className={`date-time-selector__date-pill ${
//                         isSelected ? "date-time-selector__date-pill--selected" : ""
//                       }`}
//                       onClick={() => handlePickupDatePill(d.value)}
//                     >
//                       <div className="date-time-selector__date-pill-label">
//                         {d.value === "custom" && pickupCustomLabel 
//                           ? pickupCustomLabel
//                           : d.label}
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>

//               {/* Pickup Calendar */}
//               {showPickupCalendar && (
//                 <Calendar
//                   onSelect={handlePickupCalendarSelect}
//                   onClose={() => setShowPickupCalendar(false)}
//                   minDate={new Date()}
//                   selectedDate={pickupDate}
//                 />
//               )}

//               {/* Pickup Time Selection */}
//               <div className="date-time-selector__section-label">
//                 PICKUP TIME <span>(Mumbai time)</span>
//               </div>
//               <div className="date-time-selector__time-list-container">
//                 <div className="date-time-selector__time-list">
//                   {pickupTimeSlots.map((t) => (
//                     <button
//                       key={t}
//                       className={`date-time-selector__time-slot ${
//                         pickupTime === t ? "date-time-selector__time-slot--selected" : ""
//                       }`}
//                       onClick={() => setPickupTime(t)}
//                     >
//                       {t}
//                     </button>
//                   ))}
//                 </div>
//               </div>
//             </>
//           )}
//         </div>

//         {/* Bottom Area */}
//         <div className="date-time-selector__bottom-area">
//           <button 
//             className="date-time-selector__quick-select-btn"
//             onClick={handleQuickSelect}
//           >
//             {dropOffDisplay}→ {pickUpDisplay}
//           </button>
//           <button 
//             className="date-time-selector__continue-btn"
//             onClick={handleContinue}
//           >
//             Continue
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

export default DateTimeSelector;